import {v4 as uuid} from 'uuid';

import * as validation from '../../lib/validation';
import {buildCatalogTree, buildNameListFromTreeForAnswers} from '../../lib/catalog';
import {parseVariables} from '../../lib/json-logic-utils';
import styled from "styled-components";

// Mirrors the backend MeasurementUnit enum. Keys are the enum names used as values stored
// in questionMeta; labels are the display strings shown beside question titles.
export const MEASUREMENT_UNITS = [
  {value: 'MG', label: 'mg', category: 'WEIGHT'},
  {value: 'G',  label: 'g',  category: 'WEIGHT'},
  {value: 'KG', label: 'kg', category: 'WEIGHT'},
  {value: 'OZ', label: 'oz', category: 'WEIGHT'},
  {value: 'LB', label: 'lb', category: 'WEIGHT'},
  {value: 'MT', label: 'mt', category: 'WEIGHT'},
  {value: 'ML', label: 'ml', category: 'VOLUME'},
  {value: 'L',  label: 'L',  category: 'VOLUME'},
  // Count units are only offered on inventory questions. A case only converts to itself, since case sizes vary.
  {value: 'EACH', label: 'each', category: 'COUNT'},
  {value: 'CASE', label: 'case', category: 'COUNT'}
];

export const MEASUREMENT_UNIT_CATEGORIES = [
  {value: 'WEIGHT', label: 'Weight'},
  {value: 'VOLUME', label: 'Volume'},
  {value: 'COUNT', label: 'Count'}
];

const MEASUREMENT_UNIT_LABELS = MEASUREMENT_UNITS.reduce((map, unit) => {
  map[unit.value] = unit.label;
  return map;
}, {});

export function getMeasurementUnit(question) {
  if (!question || (question.type !== 'MEASURED_NUMERIC' && !isInventoryQuestion(question))) return null;
  const value = (question.questionMeta || [])
    .filter(meta => meta.key === 'measurementUnit')
    .map(meta => meta.value)[0];
  return value || null;
}

export function getMeasurementUnitLabel(question) {
  const value = getMeasurementUnit(question);
  return value ? MEASUREMENT_UNIT_LABELS[value] || null : null;
}

// The label for a unit code, such as the unit an inventory answer was submitted in. An unknown code is shown as is.
export function getUnitLabel(value) {
  return value ? MEASUREMENT_UNIT_LABELS[value] || value : null;
}

export function getMeasurementUnitCategory(value) {
  const unit = MEASUREMENT_UNITS.filter(unit => unit.value === value)[0];
  return unit ? unit.category : null;
}

// A measured number takes a weight or volume unit. Count units are for inventory questions only.
export function hasMeasuredNumericUnit(question) {
  return ['WEIGHT', 'VOLUME'].includes(getMeasurementUnitCategory(getMeasurementUnit(question)));
}

export function getScaleTarget(question) {
  if (!question || question.type !== 'MEASURED_NUMERIC') return false;
  return (question.questionMeta || []).some(meta => meta.key === 'scaleTarget' && meta.value === 'true');
}

export function getQuestionDisplayLabel(question) {
  const unit = getMeasurementUnitLabel(question);
  return unit ? `${question.text} (${unit})` : question.text;
}

export function createQuestionOption(value, order) {
  return {value, order};
}

// TODO: Formula default answers held back till Mobile App is approved and reviewed by IOS and Android. Change back to false before PROD build
export const FORMULA_DEFAULT_ANSWERS_ENABLED = true;

// Formula default answers are stored as the JSON logic expression itself (the same format alerts and
// calculation elements use), so a stored default answer is a formula when it parses as a JSON object.
export function parseDefaultAnswerFormula(value) {
  if (typeof value !== 'string' || value.charAt(0) !== '{') {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  }
  catch (e) {
    return null;
  }
}

export function isFormulaDefaultAnswer(defaultAnswers) {
  return !!parseDefaultAnswerFormula((defaultAnswers || [])[0]);
}

// True when the formula default answer of `question` reads the answer of `target`.
function formulaDefaultAnswerReferences(question, target) {
  if (!target) {
    return false;
  }

  const formula = parseDefaultAnswerFormula((question.defaultAnswers || [])[0]);
  return !!formula && parseVariables(formula).indexOf(target.id) >= 0;
}

// The questions whose answers the formula default answer of `question` reads.
export function getFormulaDefaultAnswerVariables(question, questions) {
  const formula = parseDefaultAnswerFormula(((question || {}).defaultAnswers || [])[0]);

  if (!formula) {
    return [];
  }

  const variables = parseVariables(formula);
  return (questions || []).filter(q => q.id !== question.id && variables.indexOf(q.id) >= 0);
}

// The questions whose formula default answer reads the answer of `question`.
export function getFormulaDefaultAnswerDependents(question, questions) {
  return (questions || []).filter(q => q.id !== question.id && formulaDefaultAnswerReferences(q, question));
}

export function getLogicBuilderVariables(form, catalog, question = null, options = {}) {
  const nameOptions = buildNameListFromTreeForAnswers('', '', buildCatalogTree(catalog))
    .map(item => ({name: item.name, value: item.id}));

  form.questions.sort(function(a,b) {return a.order - b.order});

  return [...form.contextQuestions, ...form.questions]
    .filter(q => question ? q.id !== question.id : true)
    // Stop circular formula references. Only the formula default answer builder asks for this, so every
    // other logic builder keeps offering the full list.
    .filter(q => options.excludeFormulaReferences ? !formulaDefaultAnswerReferences(q, question) : true)
    .filter(q => !isQuestionDeleted(q))
    .filter(q => q.type !== 'IMAGE' && q.type !== 'SIGNATURE')
    // Sheet setup is answered before any row exists, so a sheet setup question can never read a row answer.
    .filter(q => options.excludeRowQuestions ? !!q.context : true)
    .filter(q => options.excludeNumericGrids ? q.type !== 'NUMERIC_GRID' : true)
    .filter(q => options.excludeMultiValue ? (q.type !== 'NUMERIC_GRID' && q.type !== 'MULTI_NUMERIC') : true)
    .reduce((map, question) => {
      let questionOptions = question.questionOptions;

      if (question.type === 'CATALOG_RADIO' || question.type === 'CATALOG_CHECKBOX') {
        questionOptions = nameOptions;
      }

      // The answer value of an inventory question is the amount, so its pick list items are not values to compare.
      if (isInventoryQuestion(question)) {
        questionOptions = [];
      }

      map[question.id] = {
        label: question.text,
        type: question.type,
        // Sheet setup answers are shared by every row, so a cross row total can only aggregate a
        // repeated question.
        context: !!question.context,
        isMultiValue: question.type === 'MULTI_NUMERIC' || question.type === 'NUMERIC_GRID',
        options: questionOptions.map(option => ({
          name: option.name ? option.name : option.value,
          value: option.value
        }))
      };

      return map;
    }, {});
}

const isValuePresent = validation.create((value, {isEmpty, trim}) => !isEmpty(trim(value)));

function isValidJsonLogic(expression) {
  try {
    return JSON.parse(expression) !== null;
  }
  catch (e) {
    return false;
  }
}

export function getValidationErrors(form, options = {disabled: false}) {
  const hasMultipleScaleTargets = form.questions.filter(q => !isQuestionDeleted(q) && getScaleTarget(q)).length > 1;

  return {
    name: isValuePresent(form.name) || options.disabled ? null : 'A form name is required',
    questions: form.questions.some(question => question.required && !isQuestionDeleted(question)) || options.disabled ? null : 'At least one repeated question must be required',
    questionsById: [...form.contextQuestions, ...form.questions].filter(question => !isQuestionDeleted(question)).reduce((map, question) => {
      map[question.id] = {
        text: isValuePresent(question.text) || options.disabled ? null : 'A question title is required',
        type: isValuePresent(question.type) || options.disabled ? null : 'A type is required',
        scaleTarget: !getScaleTarget(question) || options.disabled ? null
          : question.context ? 'A question shown on setup cannot be the scale target'
          : hasMultipleScaleTargets ? 'Only one question can be set as "scale target"'
          : null,
        defaultAnswers: null,
        codeGenerator: question.type === 'CODE_GENERATOR' && !options.disabled ? getCodeGeneratorValidation(question) : null,
        inventory: isInventoryQuestion(question) && !options.disabled ? getInventoryValidation(question, form) : null,
        questionOptionsByValue: hasChoices(question.type) || hasInventoryPickList(question) ? question.questionOptions.reduce((map, option) => {
          map[option.value] = null;
          
          if (!options.disabled) {
            if (!isValuePresent(option.value)) {
              map[option.value] = 'An option value is required';
            }
            else if (question.questionOptions.filter(o => o.value === option.value).length > 1) {
              map[option.value] = 'Duplicate option';
            }
          }
          
          return map;
        }, {}) : {}
      };

      return map;
    }, {}),
    alertsById: form.alerts.reduce((map, alert) => {
      map[alert.id] = {
        name: isValuePresent(alert.name) || options.disabled ? null : 'An alert name is required',
        expression: isValidJsonLogic(alert.expression) || options.disabled ? null : 'A condition to trigger this alert is required'
      };

      return map;
    }, {}),
    contextAlertsById: form.contextAlerts.reduce((map, contextAlert) => {
      map[contextAlert.id] = {
        name: isValuePresent(contextAlert.name) || options.disabled ? null : 'A contextAlert name is required',
        formId: isValuePresent(contextAlert.formId) || options.disabled ? null : 'A form to complete is required',
        description: isValuePresent(contextAlert.description) || options.disabled ? null : 'A contextAlert description is required',
        expression: isValidJsonLogic(contextAlert.expression) || options.disabled ? null : 'A condition to trigger this contextAlert is required'
      };

      return map;
    }, {}),
    // get all notificationExpressions
    notificationExpressionErrors: (() => {
      const groups = [
        form.rowCompleteContacts || [],
        form.rowApprovedContacts || [],
        form.rowRejectedContacts || []
      ];
      // flatten to each expression
      const allUserExprs = groups.flat();
      // ensure that userId is not missing
      const nullUserId = allUserExprs.some(expr => {
        return !(expr.userId || (expr.user && expr.user.id));
      });
      // if null User stop user from saving form
      return nullUserId
          ? ["A user must be selected for all notification recipients"]
          : [];
    })(),
    reviewers: form.reviewers.length === 0 && (form.rowApprovedContacts.length > 0 || form.rowRejectedContacts.length > 0)
      ? 'At least one reviewer is required' : null
  }; 
}

export function hasValidationErrors(validations) {
  if (validations === null) {
    return false;
  }

  if (Array.isArray(validations)) {
    return validations.some(validation => hasValidationErrors(validation));
  }

  if (typeof validations === 'object') {
    return Object.keys(validations)
      .map(key => validations[key])
      .some(validation => hasValidationErrors(validation));
  }

  return true;
}

export function flattenValidationErrors(validations) {
  if (validations === null) {
    return [];
  }

  if (Array.isArray(validations)) {
    return validations.reduce((acc, validation) => acc.concat(flattenValidationErrors(validation)), []);
  }

  if (typeof validations === 'object') {
    return Object.keys(validations)
      .map(key => validations[key])
      .reduce((acc, validation) => acc.concat(flattenValidationErrors(validation)), []);
  }

  return [validations];
}

// The question types with the names the form builder shows for them, in the order it lists them.
export const QUESTION_TYPES = [
  {value: 'TEXT', label: 'Text'},
  {value: 'NUMERIC', label: 'Number'},
  {value: 'MEASURED_NUMERIC', label: 'Measured Number'},
  {value: 'MULTI_NUMERIC', label: 'Multi-number'},
  {value: 'NUMERIC_GRID', label: 'Number Grid'},
  {value: 'RADIO', label: 'Single-select'},
  {value: 'CHECKBOXES', label: 'Multi-select'},
  {value: 'BOOLEAN', label: 'Yes or No'},
  {value: 'IMAGE', label: 'Photo'},
  {value: 'DATETIME', label: 'Date & Time'},
  {value: 'SIGNATURE', label: 'Signature'},
  {value: 'SCANNED_CODE', label: 'Code Scanner'},
  {value: 'CODE_GENERATOR', label: 'Code Generator'},
  {value: 'CATALOG_RADIO', label: 'Single-catalog'},
  {value: 'CATALOG_CHECKBOX', label: 'Multi-catalog'},
  {value: 'INVENTORY_RECEIVING', label: 'Inventory Receiving'},
  {value: 'INVENTORY_USAGE', label: 'Inventory Usage'}
];

// An unknown type is shown as is.
export function getQuestionTypeLabel(type) {
  const match = QUESTION_TYPES.filter(option => option.value === type)[0];
  return match ? match.label : type;
}

export function hasChoices(questionType) {
  return questionType === 'RADIO' || questionType === 'CHECKBOXES';
}


export const CODE_TYPES = [
  {value: 'JULIAN_DATE', label: 'Julian Date'},
  {value: 'DATE_CODE', label: 'Date Code'},
  {value: 'SEQUENTIAL', label: 'Sequential Number'}
];

// allowed format tokens per code type- this must match what the api validates
export const JULIAN_FORMATS = [
  {value: 'YYDDD', label: 'YYDDD'},
  {value: 'DDDYY', label: 'DDDYY'},
  {value: 'DDDYYYY', label: 'DDDYYYY'},
  {value: 'DDDY', label: 'DDDY'}
];

export const DATE_CODE_FORMATS = [
  {value: 'YYMMDD', label: 'YYMMDD'},
  {value: 'MMDDYY', label: 'MMDDYY'},
  {value: 'DDMMYY', label: 'DDMMYY'},
  {value: 'YYYYMMDD', label: 'YYYYMMDD'},
  {value: 'MMDDYYYY', label: 'MMDDYYYY'},
  {value: 'DDMMYYYY', label: 'DDMMYYYY'}
];

const ALLOWED_CODE_TYPES = CODE_TYPES.map(type => type.value);

//the max length of a prefix/suffix
export const MAX_CODE_AFFIX_LENGTH = 20;

//the widest a sequential number can be padded to. matches MAX_CODE_SEQ_PADDING on the api
export const MAX_CODE_SEQ_DIGITS = 18;

//what the sequence falls back to when the builder leaves the starting code blank
export const DEFAULT_CODE_SEQ_START = '1';

// fixed sample date used for builder previews: April 28, 2026 -> YYDDD = "26118"
// real generation happens on the mobile device using the phone's local date,
// so previews are illustrative only.
export const CODE_PREVIEW_SAMPLE_DATE = new Date(2026, 3, 28);

//true when the server issues this answer rather than the user
export function isSequentialCodeQuestion(question) {
  if (!question || question.type !== 'CODE_GENERATOR') return false;

  return getQuestionMetadata(question, 'codeType')[0] === 'SEQUENTIAL';
}

//true when one number covers the whole sheet instead of one per row. showing the question on setup is
//what decides that, since a setup answer belongs to the sheet
export function isSheetScopedCodeQuestion(question) {
  return isSequentialCodeQuestion(question) && !!question.context;
}

export function getCodeGeneratorConfig(question) {
  const read = key => getQuestionMetadata(question, key)[0] || '';
  return {
    codeType: read('codeType'),
    codePrefix: read('codePrefix'),
    codeSuffix: read('codeSuffix'),
    codeFormat: read('codeFormat'),
    codeSeqStart: read('codeSeqStart'),
    codeSeqPadding: read('codeSeqPadding'),
    codeSeqScope: read('codeSeqScope'),
    codeAllowManualEntry: read('codeAllowManualEntry')
  };
}

//the api keeps the start value and the padding as two settings, but asking a builder for both is
//confusing. they type one starting code and the zeros decide the width, so 001 counts 001, 002, 003
export function toStartingCode(config) {
  if (!config.codeSeqStart) return '';

  const width = parseInt(config.codeSeqPadding, 10);
  if (!width || width < 1) return config.codeSeqStart;

  return config.codeSeqStart.padStart(width, '0');
}

//splits a typed starting code back into the two settings the api expects
export function fromStartingCode(startingCode) {
  const trimmed = (startingCode || '').trim();

  //clearing the box has to actually clear it so the builder can retype. validation catches the gap
  if (!trimmed) {
    return {codeSeqStart: '', codeSeqPadding: ''};
  }

  //keep whatever was typed if it is not a plain number, so validation can explain the problem
  if (!/^\d+$/.test(trimmed)) {
    return {codeSeqStart: trimmed, codeSeqPadding: String(trimmed.length)};
  }

  return {codeSeqStart: String(parseInt(trimmed, 10)), codeSeqPadding: String(trimmed.length)};
}

function dayOfYear(date) {
  // compute in UTC so daylight-saving transitions between Jan 1 and the date does not
  // shift the result by a day.
  const startOfYear = Date.UTC(date.getFullYear(), 0, 0);
  const current = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.floor((current - startOfYear) / 86400000);
}

// approximates the mobile app's on-device date formatting for a single date so the
// builder can show a preview. greedy longest-token match keeps DDD (day-of-year) and
// YYYY from being shadowed by DD / YY.
function applyDateFormat(format, date) {
  const yyyy = String(date.getFullYear());
  const tokens = [
    ['YYYY', yyyy],
    ['DDD', String(dayOfYear(date)).padStart(3, '0')],
    ['MM', String(date.getMonth() + 1).padStart(2, '0')],
    ['DD', String(date.getDate()).padStart(2, '0')],
    ['YY', yyyy.slice(-2)],
    ['Y', yyyy.slice(-1)]
  ];

  let result = '';
  let i = 0;
  while (i < format.length) {
    let matched = false;
    for (let t = 0; t < tokens.length; t++) {
      const [token, value] = tokens[t];
      if (format.startsWith(token, i)) {
        result += value;
        i += token.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      result += format[i];
      i += 1;
    }
  }
  return result;
}

// a best effort preview of the code a question would generate. Returns '' when the
// config is too incomplete to preview.
export function createCodePreview(config, sampleDate = CODE_PREVIEW_SAMPLE_DATE) {
  if (!config || !config.codeType) return '';

  const prefix = config.codePrefix || '';
  const suffix = config.codeSuffix || '';

  //worked out from the config alone. asking the api would take a number out of the real series just to
  //draw a form builder screen
  if (config.codeType === 'SEQUENTIAL') {
    const startingCode = toStartingCode(config);
    if (!startingCode) return '';

    return `${prefix}${startingCode}${suffix}`;
  }

  if (config.codeType !== 'JULIAN_DATE' && config.codeType !== 'DATE_CODE') return '';
  if (!config.codeFormat) return '';

  const core = applyDateFormat(config.codeFormat, sampleDate);

  return `${prefix}${core}${suffix}`;
}

//the two codes after the first, so the builder can see the series counting up
export function createSequencePreview(config) {
  if (!config || config.codeType !== 'SEQUENTIAL') return [];

  const start = parseInt(config.codeSeqStart, 10);
  const width = parseInt(config.codeSeqPadding, 10) || 1;
  if (isNaN(start)) return [];

  const prefix = config.codePrefix || '';
  const suffix = config.codeSuffix || '';

  return [start + 1, start + 2]
    .map(value => `${prefix}${String(value).padStart(width, '0')}${suffix}`);
}

// validates the CODE_GENERATOR config, mirroring the rules the api enforces on a save
export function getCodeGeneratorValidation(question) {
  const config = getCodeGeneratorConfig(question);
  const errors = {
    codeType: null, codeFormat: null, codePrefix: null, codeSuffix: null, codeSeqStart: null
  };

  // the prefix and suffix are both optional, but when set each is capped independently
  if (config.codePrefix.length > MAX_CODE_AFFIX_LENGTH) {
    errors.codePrefix = `Prefix can be at most ${MAX_CODE_AFFIX_LENGTH} characters`;
  }
  if (config.codeSuffix.length > MAX_CODE_AFFIX_LENGTH) {
    errors.codeSuffix = `Suffix can be at most ${MAX_CODE_AFFIX_LENGTH} characters`;
  }

  if (!isValuePresent(config.codeType)) {
    errors.codeType = 'A code type is required';
    return errors;
  }

  if (!ALLOWED_CODE_TYPES.includes(config.codeType)) {
    errors.codeType = 'Invalid code type';
    return errors;
  }

  //no date format to pick here. what it needs instead is a sensible number to start counting from
  if (config.codeType === 'SEQUENTIAL') {
    const startingCode = toStartingCode(config);

    if (!isValuePresent(startingCode)) {
      errors.codeSeqStart = 'A starting code is required';
    }
    else if (!/^\d+$/.test(startingCode)) {
      errors.codeSeqStart = 'The starting code can only contain digits';
    }
    else if (startingCode.length > MAX_CODE_SEQ_DIGITS) {
      errors.codeSeqStart = `The starting code can be at most ${MAX_CODE_SEQ_DIGITS} digits`;
    }

    return errors;
  }

  const allowed = config.codeType === 'JULIAN_DATE' ? JULIAN_FORMATS : DATE_CODE_FORMATS;
  if (!isValuePresent(config.codeFormat)) {
    errors.codeFormat = 'A format is required';
  }
  else if (!allowed.some(format => format.value === config.codeFormat)) {
    errors.codeFormat = 'Invalid format for this code type';
  }

  return errors;
}

// Inventory questions record an amount of an item (and its lot) received or used. The amount is the answer value, the
// item and lot are sent beside it.
export const INVENTORY_TYPES = ['INVENTORY_RECEIVING', 'INVENTORY_USAGE'];

export const MEASUREMENT_UNIT_KEY = 'measurementUnit';
export const INVENTORY_ITEM_SOURCE_KEY = 'inventoryItemSource';
export const INVENTORY_LOT_SOURCE_KEY = 'inventoryLotSource';
export const INVENTORY_LOT_QUESTION_KEY = 'inventoryLotQuestionId';
export const INVENTORY_META_KEYS = [INVENTORY_ITEM_SOURCE_KEY, INVENTORY_LOT_SOURCE_KEY, INVENTORY_LOT_QUESTION_KEY];

// Mirrors the api. These can't be changed once the form is published.
export const INVENTORY_STRUCTURAL_KEYS = [MEASUREMENT_UNIT_KEY, ...INVENTORY_META_KEYS];

export const INVENTORY_ITEM_SOURCES = [
  {value: 'CATALOG', label: 'Single-catalog', description: 'Picked from your catalog.'},
  {value: 'PICKLIST', label: 'Single-select', description: 'Picked from the options below.'},
  {value: 'TEXT', label: 'Text', description: 'Typed in by the user.'}
];

// Only a receiving question has a lot setting. A usage question takes the lot from the stock it is used from.
export const INVENTORY_LOT_SOURCES = [
  {value: 'FIELD', label: 'Entered here', description: 'The lot is entered on this question.'},
  {value: 'QUESTION', label: 'Another question', description: 'The lot is read from another question on this form.'}
];

// The units a published inventory question can move to: the ones its amounts convert into. A case only converts to
// itself, since case sizes vary, so a count unit stays as it is.
export function getConvertibleUnits(unit) {
  const category = getMeasurementUnitCategory(unit);

  if (!category || category === 'COUNT') {
    return MEASUREMENT_UNITS.filter(u => u.value === unit);
  }

  return MEASUREMENT_UNITS.filter(u => u.category === category);
}

// The question types the api accepts as the lot for an inventory question.
export const INVENTORY_LOT_QUESTION_TYPES = ['TEXT', 'NUMERIC', 'SCANNED_CODE', 'CODE_GENERATOR', 'RADIO'];

export function isInventoryType(type) {
  return INVENTORY_TYPES.includes(type);
}

export function isInventoryQuestion(question) {
  return !!question && isInventoryType(question.type);
}

// Questions listed from other forms may come without any metadata, so these read it defensively.
function readMetadata(question, key) {
  return ((question && question.questionMeta) || [])
    .filter(metadata => metadata.key === key)
    .map(metadata => metadata.value)[0] || null;
}

export function getInventoryItemSource(question) {
  return isInventoryQuestion(question) ? readMetadata(question, INVENTORY_ITEM_SOURCE_KEY) : null;
}

export function getInventoryLotSource(question) {
  return isInventoryQuestion(question) ? readMetadata(question, INVENTORY_LOT_SOURCE_KEY) : null;
}

// True for a question whose lot comes from the Lot setting: a receiving question. A usage question takes the lot
// from the stock it is used from, so its lot settings are not shown.
export function hasInventoryLotSetting(question) {
  return !!question && question.type === 'INVENTORY_RECEIVING';
}

// A usage question with its lot settings put back to the default. The api still asks every inventory question for a
// lot source, so a usage question keeps "Entered here", and never points at a lot question: that would block deleting
// the question it points at with no setting to change. Every other question is returned as it is.
export function withInventoryLotNormalised(question) {
  if (!question || question.type !== 'INVENTORY_USAGE') {
    return question;
  }

  const questionMeta = (question.questionMeta || []).filter(metadata =>
    metadata.key !== INVENTORY_LOT_SOURCE_KEY && metadata.key !== INVENTORY_LOT_QUESTION_KEY);

  return {...question, questionMeta: [...questionMeta, {key: INVENTORY_LOT_SOURCE_KEY, value: 'FIELD'}]};
}

// The id of the question whose answer is the lot, or null when the lot is entered on the inventory question itself.
export function getInventoryLotQuestionId(question) {
  return getInventoryLotSource(question) === 'QUESTION' ? readMetadata(question, INVENTORY_LOT_QUESTION_KEY) : null;
}

// An inventory pick list keeps its items in questionOptions, like a single-select question.
export function hasInventoryPickList(question) {
  return getInventoryItemSource(question) === 'PICKLIST';
}

// The questions on the form that can be the lot for `question`.
export function getInventoryLotQuestionCandidates(form, question) {
  return [...form.contextQuestions, ...form.questions]
    .filter(q => !isQuestionDeleted(q) && q.id !== question.id && INVENTORY_LOT_QUESTION_TYPES.includes(q.type))
    .sort((a, b) => a.order - b.order);
}

// The inventory questions on the form that read their lot from `question`.
export function getInventoryQuestionsUsingLot(form, question) {
  return [...form.contextQuestions, ...form.questions]
    .filter(q => !isQuestionDeleted(q) && getInventoryLotQuestionId(q) === question.id);
}

// Points the lot of an inventory question at a new question id. When the lot question isn't in idMap (for example a
// question imported from another form), the lot goes back to being entered on the question itself.
export function withInventoryLotRemapped(question, idMap) {
  const lotQuestionId = getInventoryLotQuestionId(question);

  if (!lotQuestionId || question.type === 'INVENTORY_USAGE') {
    return withInventoryLotNormalised(question);
  }

  const nextId = idMap[lotQuestionId];
  const questionMeta = question.questionMeta.filter(metadata =>
    metadata.key !== INVENTORY_LOT_SOURCE_KEY && metadata.key !== INVENTORY_LOT_QUESTION_KEY);

  return {
    ...question,
    questionMeta: nextId
      ? [...questionMeta, {key: INVENTORY_LOT_SOURCE_KEY, value: 'QUESTION'}, {key: INVENTORY_LOT_QUESTION_KEY, value: nextId}]
      : [...questionMeta, {key: INVENTORY_LOT_SOURCE_KEY, value: 'FIELD'}]
  };
}

// Mirrors the inventory rules the api enforces on a save.
export function getInventoryValidation(question, form) {
  const errors = {
    measurementUnit: null, itemSource: null, questionOptions: null, lotSource: null, lotQuestionId: null, question: null
  };
  const unit = getMeasurementUnit(question);
  const itemSource = getInventoryItemSource(question);
  const lotSource = getInventoryLotSource(question);

  if (question.context) {
    errors.question = 'Inventory questions can\'t be shown on setup';
  }
  else if ((question.defaultAnswers || []).length > 0 || question.lockDefault) {
    errors.question = 'Inventory questions can\'t have a default answer';
  }

  if (!unit) {
    errors.measurementUnit = 'A unit is required';
  }
  else if (!getMeasurementUnitCategory(unit)) {
    errors.measurementUnit = 'Choose a unit from the list';
  }

  if (!INVENTORY_ITEM_SOURCES.some(source => source.value === itemSource)) {
    errors.itemSource = 'Choose how the item is answered';
  }
  else if (itemSource === 'PICKLIST' && question.questionOptions.length < 1) {
    errors.questionOptions = 'Add at least one option';
  }

  // a usage question has no lot setting to check: it is kept at the default (withInventoryLotNormalised), and the
  // lot comes from the stock picked
  if (hasInventoryLotSetting(question)) {
    if (!INVENTORY_LOT_SOURCES.some(source => source.value === lotSource)) {
      errors.lotSource = 'Choose where the lot comes from';
    }
    else if (lotSource === 'QUESTION') {
      const lotQuestionId = getInventoryLotQuestionId(question);

      if (!lotQuestionId) {
        errors.lotQuestionId = 'Choose the question that holds the lot';
      }
      else if (!getInventoryLotQuestionCandidates(form, question).some(q => q.id === lotQuestionId)) {
        errors.lotQuestionId = 'The lot must come from a text, number, code scanner, code generator or single-select question on this form';
      }
    }
  }

  return errors;
}

export function createQuestion(order) {
  return {
    id: uuid(),
    order,
    context: false,
    text: '',
    type: '',
    defaultAnswers: [],
    required: true,
    questionMeta: [],
    questionOptions: [],
    condition: null
  };
}

export function getQuestionMetadata(question, key) {
  return question.questionMeta
    .filter(metadata => metadata.key === key)
    .map(metadata => metadata.value);
}

// Usage in other files to check if question was soft deleted from a published form
export function isQuestionDeleted(question){
  return question.deleted;
}

// Shared answers let a question offer the submitted answers of questions on other forms as extra options. Both keys
// are only changed through the sharedAnswers block sent with a form save (see toSavedQuestion); the api ignores them in
// the plain metadata list.
export const REUSE_MODE_KEY = 'reuseMode';
export const RECEIVES_SHARED_ANSWERS_KEY = 'receivesSharedAnswers';
export const SHARED_ANSWER_META_KEYS = [REUSE_MODE_KEY, RECEIVES_SHARED_ANSWERS_KEY];

// Mirrors the source types the api accepts.
export const SHARED_ANSWER_SOURCE_TYPES = ['TEXT', 'NUMERIC', 'MEASURED_NUMERIC', 'RADIO', 'SCANNED_CODE', 'CODE_GENERATOR', 'INVENTORY_RECEIVING'];

// Mirrors the receiving types the api accepts.
export const SHARED_ANSWER_RECEIVING_TYPES = ['RADIO', 'INVENTORY_USAGE'];

export const REUSE_MODES = [
  {value: 'reusable', label: 'Reusable', description: 'Each answer stays available on linked forms, so it can be picked any number of times.'},
  {value: 'one_time', label: 'One-time use', description: 'Each answer can be picked once on a linked form, then it is no longer offered.'}
];

export function getReuseModeLabel(reuseMode) {
  const mode = REUSE_MODES.filter(mode => mode.value === reuseMode)[0];
  return mode ? mode.label : reuseMode;
}

// Questions listed from other forms may come without any metadata, so these read it defensively.
function getSharedAnswerMetadata(question, key) {
  return (question.questionMeta || [])
    .filter(metadata => metadata.key === key)
    .map(metadata => metadata.value);
}

export function getReuseMode(question) {
  return getSharedAnswerMetadata(question, REUSE_MODE_KEY)[0] || null;
}

export function receivesSharedAnswers(question) {
  return getSharedAnswerMetadata(question, RECEIVES_SHARED_ANSWERS_KEY).includes('true');
}

export function canBeSharedAnswerSource(question) {
  return SHARED_ANSWER_SOURCE_TYPES.includes(question.type);
}

// Only single-select and inventory usage row questions can receive shared answers in this version.
export function canReceiveSharedAnswers(question) {
  return SHARED_ANSWER_RECEIVING_TYPES.includes(question.type) && !question.context;
}

// The ways a source question can share its answers. Received inventory is only ever shared as reusable, since the
// amount left is tracked by the inventory ledger instead.
// Every mode is listed so people can see what each one means. A receiving question is always reusable, since the
// inventory tracks how much of each lot is left, so its one-time mode is shown but can't be picked.
export function getReuseModesFor(question) {
  if (question.type !== 'INVENTORY_RECEIVING') {
    return REUSE_MODES;
  }

  return REUSE_MODES.map(mode => mode.value === 'one_time'
    ? {...mode, unavailable: 'Not available for inventory. The amount left in each lot is tracked instead.'}
    : mode);
}

// Mirrors the api's pairing rules: an inventory usage question only draws from reusable inventory receiving questions
// whose unit is the same kind (weight, volume or count), and inventory receiving questions only feed usage questions.
export function canLinkSource(receiving, source) {
  if (receiving.type === 'INVENTORY_USAGE') {
    const receivingCategory = getMeasurementUnitCategory(getMeasurementUnit(receiving));

    return source.type === 'INVENTORY_RECEIVING'
      && getReuseMode(source) === 'reusable'
      && !!receivingCategory
      && receivingCategory === getMeasurementUnitCategory(getMeasurementUnit(source));
  }

  return source.type !== 'INVENTORY_RECEIVING';
}

// Applies a change to a question's shared answer settings. Only the keys given change: reuseMode (null stops sharing),
// receivesSharedAnswers, sources (the whole list of linked sources), addSource and removeSourceId. The change is saved
// with the form.
export function withSharedAnswerSettings(question, {reuseMode, receivesSharedAnswers: receives, sources, addSource, removeSourceId}) {
  const nextReuseMode = reuseMode === undefined ? getReuseMode(question) : reuseMode;
  const nextReceives = receives === undefined ? receivesSharedAnswers(question) : receives;
  let nextSources = sources === undefined ? question.sharedAnswerSources : sources;

  if (addSource && Array.isArray(nextSources)) {
    nextSources = [...nextSources.filter(s => s.sourceQuestionId !== addSource.sourceQuestionId), addSource];
  }
  if (removeSourceId && Array.isArray(nextSources)) {
    nextSources = nextSources.filter(s => s.sourceQuestionId !== removeSourceId);
  }

  return {
    ...question,
    questionMeta: [
      ...(question.questionMeta || []).filter(metadata => !SHARED_ANSWER_META_KEYS.includes(metadata.key)),
      ...(nextReuseMode ? [{key: REUSE_MODE_KEY, value: nextReuseMode}] : []),
      ...(nextReceives ? [{key: RECEIVES_SHARED_ANSWERS_KEY, value: 'true'}] : [])
    ],
    sharedAnswerSources: nextSources,
    sharedAnswersChanged: true
  };
}

// True when a question's shared answer changes can't be saved, because it receives shared answers but its linked sources
// never loaded. Saving it would replace the links it has with an empty list.
export function hasUnsavableSharedAnswers(question) {
  return !!question.sharedAnswersChanged && receivesSharedAnswers(question) && !Array.isArray(question.sharedAnswerSources);
}

// The question as the api expects it in a form save. A question whose shared answer settings were changed carries
// them in sharedAnswers; one that was not changed leaves it out, so the api keeps what it has.
export function toSavedQuestion(question) {
  const {sharedAnswerSources, sharedAnswersChanged, ...saved} = question;

  if (!sharedAnswersChanged) {
    return saved;
  }

  return {
    ...saved,
    sharedAnswers: {
      reuseMode: getReuseMode(question),
      receivesSharedAnswers: receivesSharedAnswers(question),
      sourceQuestionIds: (sharedAnswerSources || []).map(source => source.sourceQuestionId)
    }
  };
}

// A copied or imported question is a new question, so it starts without any shared answer settings.
export function withoutSharedAnswerSettings(question) {
  const {sharedAnswerSources, sharedAnswersChanged, ...copy} = question;

  return {
    ...copy,
    questionMeta: (question.questionMeta || []).filter(metadata => !SHARED_ANSWER_META_KEYS.includes(metadata.key))
  };
}

export const WidthChangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  > div:first-child {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  label {
    font-weight: 500;
    color: #333;
    font-size: 0.9rem;
  }
`;


export const SegmentedControl = styled.div`
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  overflow: hidden;
  background: #ffffff;
`;

export const SegmentOption = styled.button`
  padding: 0.375rem 0.75rem;
  border: none;
  background: ${props => props.active ? '#3b82f6' : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : '#374151'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  border-right: 1px solid #d1d5db;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f3f4f6'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    z-index: 1;
    position: relative;
  }
`;

export const WidthWarning = styled.div`
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
  margin-left: 0.5rem;
`;

export const WidthLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: #333;
`;